import {
  AlertOutlined,
  ApartmentOutlined,
  BarChartOutlined,
  CalendarOutlined,
  CarOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  InboxOutlined,
  FileTextOutlined,
  GlobalOutlined,
  MedicineBoxOutlined,
  HistoryOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SafetyOutlined,
  ScheduleOutlined,
  TeamOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import type { Permission } from '@tms/shared';
import { PERMISSIONS } from '@tms/shared';
import { Dropdown, Layout, Menu, Select, Space, Tag, Tooltip, Typography } from 'antd';
import type { SelectProps } from 'antd';

import { HeaderClock } from '@/components/HeaderClock';
import { UserAvatar } from '@/components/UserAvatar';
import { UserCard } from '@/components/UserCard';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/AuthContext';
import { LocaleFlag } from '@/components/LocaleFlag';
import { OfficeLogo } from '@/components/OfficeLogo';
import i18n, { LOCALE_STORAGE_KEY, SUPPORTED_LOCALES, localeDescriptor } from '@/i18n';
import { roleLabel } from '@/lib/labels';
import { OPEN_GROUPS_KEY } from '@/lib/storageKeys';

const { Header, Sider, Content } = Layout;

const COLLAPSED_KEY = 'tms.sidebarCollapsed';
const WIDTH_KEY = 'tms.sidebarWidth';
const DEFAULT_SIDER_WIDTH = 220;
const MIN_SIDER_WIDTH = 180;
const MAX_SIDER_WIDTH = 400;

export function AppLayout() {
  const { t } = useTranslation();
  const { user, logout, switchOffice, can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Состояние меню запоминается: диспетчер, свернувший меню ради ширины
  // таблицы, не должен разворачивать его заново после каждого входа.
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSED_KEY) === '1',
  );

  const toggleCollapsed = (): void => {
    setCollapsed((previous) => {
      localStorage.setItem(COLLAPSED_KEY, previous ? '0' : '1');
      return !previous;
    });
  };

  // Ширина меню тоже на память: кто раздвинул его под длинные названия
  // разделов, не должен настраивать заново при каждом входе.
  const [siderWidth, setSiderWidth] = useState(() => {
    const stored = Number(localStorage.getItem(WIDTH_KEY));
    return stored >= MIN_SIDER_WIDTH && stored <= MAX_SIDER_WIDTH ? stored : DEFAULT_SIDER_WIDTH;
  });
  const resizing = useRef(false);

  useEffect(() => {
    const onMouseMove = (event: MouseEvent): void => {
      if (!resizing.current) return;
      setSiderWidth(Math.min(MAX_SIDER_WIDTH, Math.max(MIN_SIDER_WIDTH, event.clientX)));
    };
    const onMouseUp = (): void => {
      if (!resizing.current) return;
      resizing.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // Курсор уже отпущен — текущее значение состояния и есть то, что
      // нужно запомнить; функциональная форма читает его без доп. зависимостей.
      setSiderWidth((current) => {
        localStorage.setItem(WIDTH_KEY, String(current));
        return current;
      });
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const startResize = (event: React.MouseEvent): void => {
    // Текст не должен выделяться по всей странице, пока тянешь за край —
    // без этого курсор «утягивает» за собой подсветку всего, что под ним.
    event.preventDefault();
    resizing.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  // Разделы, на которые нет прав, из меню убираются: пункт, ведущий
  // на экран «нет прав», только раздражает. Права всё равно проверяются
  // на сервере — меню лишь отражает их.
  interface MenuLeaf {
    key: string;
    icon: JSX.Element;
    label: string;
    permission?: Permission;
  }
  interface MenuGroup {
    key: string;
    icon: JSX.Element;
    label: string;
    children: MenuLeaf[];
  }

  const allItems: Array<MenuLeaf | MenuGroup> = [
    { key: '/', icon: <DashboardOutlined />, label: t('Главная') },
    {
      key: 'group-hr',
      icon: <IdcardOutlined />,
      label: t('HR'),
      children: [
        {
          key: '/employees',
          icon: <IdcardOutlined />,
          label: t('Сотрудники'),
          permission: PERMISSIONS.EMPLOYEE_READ,
        },
        {
          key: '/positions',
          icon: <ApartmentOutlined />,
          label: t('Структура'),
          permission: PERMISSIONS.DICTIONARY_READ,
        },
        {
          key: '/attendance',
          icon: <CalendarOutlined />,
          label: t('Табель'),
          permission: PERMISSIONS.ATTENDANCE_READ,
        },
      ],
    },
    {
      key: 'group-fleet',
      icon: <CarOutlined />,
      label: t('Автопарк'),
      children: [
        {
          key: '/vehicles',
          icon: <CarOutlined />,
          label: t('Транспорт'),
          permission: PERMISSIONS.VEHICLE_READ,
        },
        {
          key: '/drivers',
          icon: <TeamOutlined />,
          label: t('Водители'),
          permission: PERMISSIONS.DRIVER_READ,
        },
        {
          key: '/technical',
          icon: <ToolOutlined />,
          label: t('Техконтроль'),
          permission: PERMISSIONS.VEHICLE_READ,
        },
        {
          key: '/telemetry',
          icon: <EnvironmentOutlined />,
          label: t('Телеметрия'),
          permission: PERMISSIONS.TELEMETRY_READ,
        },
      ],
    },
    {
      key: 'group-operations',
      icon: <FileTextOutlined />,
      label: t('Эксплуатация'),
      children: [
        {
          key: '/transport-requests',
          icon: <ScheduleOutlined />,
          label: t('Заявки'),
          permission: PERMISSIONS.TRANSPORT_REQUEST_READ,
        },
        {
          key: '/waybills',
          icon: <FileTextOutlined />,
          label: t('Путевые листы'),
          permission: PERMISSIONS.WAYBILL_READ,
        },
        {
          key: '/fuel',
          icon: <ToolOutlined />,
          label: t('ГСМ'),
          permission: PERMISSIONS.FUEL_READ,
        },
        {
          key: '/medical',
          icon: <MedicineBoxOutlined />,
          label: t('Здравпункт'),
          permission: PERMISSIONS.DRIVER_READ,
        },
        {
          key: '/violations',
          icon: <AlertOutlined />,
          label: t('Нарушения'),
          permission: PERMISSIONS.VIOLATION_READ,
        },
      ],
    },
    {
      key: '/stock',
      icon: <InboxOutlined />,
      label: t('Склад ТМЦ'),
      permission: PERMISSIONS.STOCK_READ,
    },
    {
      key: '/reports',
      icon: <BarChartOutlined />,
      label: t('Отчёты'),
      permission: PERMISSIONS.REPORT_READ,
    },
    {
      key: 'group-admin',
      icon: <DatabaseOutlined />,
      label: t('Администрирование'),
      children: [
        {
          key: '/users',
          icon: <SafetyOutlined />,
          label: t('Пользователи'),
          permission: PERMISSIONS.USER_READ,
        },
        {
          key: '/admin',
          icon: <DatabaseOutlined />,
          label: t('Справочники'),
          permission: PERMISSIONS.DICTIONARY_READ,
        },
        {
          key: '/audit',
          icon: <HistoryOutlined />,
          label: t('Журнал действий'),
          permission: PERMISSIONS.AUDIT_READ,
        },
      ],
    },
  ];

  // «Глобальные настройки» — не в общем списке, а отдельным выделенным
  // пунктом внизу меню: там — работа своего офиса, здесь — все организации
  // платформы сразу. Право platform.manage есть только у суперадминистратора,
  // и выдать его другой роли может тоже только он (проверка на сервере).
  const globalSettingsItems = can(PERMISSIONS.PLATFORM_MANAGE)
    ? [{ key: '/platform', icon: <GlobalOutlined />, label: t('Глобальные настройки') }]
    : [];

  // Группа без прав ни на один из пунктов сама исчезает из меню — иначе
  // осталась бы пустая стрелка раскрытия, ведущая в никуда.
  const menuItems = allItems
    .map((item) => {
      if ('children' in item) {
        const children = item.children
          .filter((child) => !child.permission || can(child.permission))
          .map(({ key, icon, label }) => ({ key, icon, label }));
        return children.length > 0 ? { key: item.key, icon: item.icon, label: item.label, children } : null;
      }
      return !item.permission || can(item.permission) ? { key: item.key, icon: item.icon, label: item.label } : null;
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  // Группа с активным сейчас разделом внутри раскрывается сама — переход
  // по ссылке из другого места (не по клику в меню) не должен оставлять
  // пользователя перед свёрнутой стрелкой с неясно куда делась страница.
  const currentKey = location.search ? location.pathname + location.search : location.pathname;
  const activeGroupKey = allItems.find(
    (item) => 'children' in item && item.children.some((child) => child.key === currentKey),
  )?.key;

  const [openKeys, setOpenKeys] = useState<string[]>(() => {
    const stored = JSON.parse(localStorage.getItem(OPEN_GROUPS_KEY) ?? '[]') as string[];
    return activeGroupKey && !stored.includes(activeGroupKey) ? [...stored, activeGroupKey] : stored;
  });

  useEffect(() => {
    if (activeGroupKey) {
      setOpenKeys((prev) => (prev.includes(activeGroupKey) ? prev : [...prev, activeGroupKey]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGroupKey]);

  const changeLocale = (locale: string): void => {
    // Запись в localStorage делает выбор «явным»: с этого момента язык
    // из профиля его больше не перебивает (см. AuthContext).
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    void i18n.changeLanguage(locale);
  };

  const activeOffice = user?.activeOffice;

  /*
    Офисы в переключателе группируются по организациям — но только когда
    организаций больше одной: у сотрудника единственной организации лишний
    заголовок над списком только занимал бы место.
  */
  const officeOptions: NonNullable<SelectProps<number>['options']> = (() => {
    const offices = user?.availableOffices ?? [];
    const toOption = (office: (typeof offices)[number]) => ({
      value: office.id,
      label: `${office.code} — ${office.name}`,
    });

    const organizations = new Map<number, { name: string; options: ReturnType<typeof toOption>[] }>();
    for (const office of offices) {
      const key = office.organization?.id ?? 0;
      const group = organizations.get(key) ?? {
        name: office.organization?.name ?? '',
        options: [],
      };
      group.options.push(toOption(office));
      organizations.set(key, group);
    }

    if (organizations.size < 2) return offices.map(toOption);
    return [...organizations.values()].map((group) => ({
      label: group.name,
      options: group.options,
    }));
  })();

  /*
   * Должность под именем пользователя.
   *
   * Ролей может быть несколько — человек бывает диспетчером и механиком
   * одновременно, — поэтому перечисляем все. Незнакомый код роли выводим
   * как есть: роли заводятся администратором, и подписи для новых в словаре
   * не будет. Перерисовка при смене языка приходит от useTranslation выше,
   * поэтому i18n.language здесь всегда актуален.
   */
  const roleTitle =
    user?.roles?.length
      ? [...new Set(user.roles.map((code) => roleLabel(code, i18n.language)))].join(', ')
      : t('Роль не назначена');

  return (
    /*
      Шапка — верхний уровень разметки, а меню лежит под ней.
      Так шапка занимает всю ширину окна и не зависит от того, свёрнуто
      меню или нет: при переключении разделов она остаётся неподвижной.
    */
    <Layout style={{ minHeight: '100vh' }}>
      <Header
        className="tms-header"
        style={{
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingInline: 16,
          gap: 12,
        }}
      >
        {/*
          Переключатель офиса вынесен в шапку намеренно: пользователь
          должен постоянно видеть, данные какого аэропорта перед ним.
          Ошибка «внёс путевой лист не в тот офис» стоит дорого.
        */}
        <Space size="middle">
          {/* Логотип рядом с названием офиса: связка «эмблема — название»
              читается как одно целое и не теряется при сворачивании меню. */}
          <OfficeLogo
            officeId={activeOffice?.id}
            code={activeOffice?.iataCode ?? activeOffice?.code ?? 'ГСМ'}
            height={40}
            variant="light"
          />

          <Select
            value={user?.activeOffice.id}
            style={{ minWidth: 240 }}
            showSearch
            optionFilterProp="label"
            onChange={(officeId) => void switchOffice(officeId)}
            options={officeOptions}
            disabled={(user?.availableOffices.length ?? 0) < 2}
          />
          <Tag color="blue">{user?.activeOffice.iataCode ?? user?.activeOffice.code}</Tag>
        </Space>

        <Space size="middle">
          <HeaderClock />

          <Select
            value={localeDescriptor(i18n.language).code}
            style={{ width: 140 }}
            onChange={changeLocale}
            optionLabelProp="label"
            options={SUPPORTED_LOCALES.map((locale) => ({
              value: locale.code,
              label: (
                <Space size={8}>
                  <LocaleFlag code={locale.flag} />
                  {locale.label}
                </Space>
              ),
            }))}
          />

          {/* Наведение на аватарку открывает карточку сотрудника: контакты,
              офис и, отдельно за чертой, кнопка выхода. */}
          <Dropdown
            trigger={['hover']}
            placement="bottomRight"
            // Небольшая задержка закрытия: курсор доезжает до карточки,
            // не теряя её по пути через зазор под шапкой.
            mouseLeaveDelay={0.25}
            popupRender={() =>
              user ? (
                <UserCard user={user} roleTitle={roleTitle} onLogout={() => void logout()} />
              ) : (
                <></>
              )
            }
          >
            {/* Фотография рядом с именем: по лицу свою учётку узнают быстрее,
                чем по строке текста, — особенно на общем рабочем месте,
                где за компьютер садятся посменно.

                Должность в подсказке, а не в строке: на общем рабочем месте
                важно с одного взгляда убедиться, под чьими правами работаешь,
                но в шапке для неё нет места. */}
            <Space style={{ cursor: 'pointer' }} size={10}>
              <UserAvatar
                userId={user?.id}
                fullName={user?.fullName}
                photoKey={user?.photoKey}
                size={36}
              />
              {/* Должность строкой под именем, а не подсказкой при наведении:
                  всплывающая подсказка перекрывалась выпадающим меню выхода,
                  которое открывается по тому же наведению. */}
              <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
                <Typography.Text strong>{user?.fullName}</Typography.Text>
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: 12 }}
                  // Ролей может быть несколько; в строку помещается не всё,
                  // поэтому длинный перечень обрезается многоточием.
                  ellipsis={{ tooltip: false }}
                >
                  {roleTitle}
                </Typography.Text>
              </div>
            </Space>
          </Dropdown>

        </Space>
      </Header>

      <Layout>
        <Sider
          className="tms-sider"
          theme="dark"
          breakpoint="lg"
          collapsedWidth={64}
          width={siderWidth}
          collapsed={collapsed}
          onCollapse={(value) => {
            // Срабатывает и при переходе через контрольную точку ширины:
            // сохраняем, чтобы состояние не разошлось с кнопкой.
            localStorage.setItem(COLLAPSED_KEY, value ? '1' : '0');
            setCollapsed(value);
          }}
        >
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[currentKey]}
            openKeys={collapsed ? undefined : openKeys}
            onOpenChange={(keys) => {
              setOpenKeys(keys);
              localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(keys));
            }}
            items={menuItems}
            onClick={({ key }) => navigate(key)}
          />

          {globalSettingsItems.length > 0 && (
            <Menu
              className="tms-sider-global"
              theme="dark"
              mode="inline"
              selectedKeys={[currentKey]}
              items={globalSettingsItems}
              onClick={({ key }) => navigate(key)}
            />
          )}

          {/* Версия платформы — техническая подпись внизу меню, а не пункт
              интерфейса: полезна в основном при обращении в поддержку («на какой
              версии вы работаете»). В свёрнутом меню остаётся только номер версии. */}
          <Tooltip
            title={`${t('Версия платформы')}: v${__APP_VERSION__} · ${__BUILD_ID__}`}
            placement="right"
          >
            <div className="tms-sider-version">
              <span>v{__APP_VERSION__}</span>
              {!collapsed && <span>{__BUILD_ID__}</span>}
            </div>
          </Tooltip>

          {/*
            Кнопка сворачивания прижата к низу меню: список разделов
            растягивается на всю доступную высоту (см. styles.css), поэтому
            кнопка оказывается под ним независимо от числа пунктов.
          */}
          <Tooltip
            title={collapsed ? t('Развернуть меню') : t('Свернуть меню')}
            placement="right"
          >
            <button
              type="button"
              className="tms-sider-toggle"
              aria-label={collapsed ? t('Развернуть меню') : t('Свернуть меню')}
              onClick={toggleCollapsed}
            >
              {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              {!collapsed && <span>{t('Свернуть меню')}</span>}
            </button>
          </Tooltip>

          {/* Захват растягивания — только в развёрнутом виде: в свёрнутом
              меню (64px, одни иконки) тянуть не за что и некуда. */}
          {!collapsed && (
            <div
              className="tms-sider-resize-handle"
              role="separator"
              aria-orientation="vertical"
              aria-label={t('Изменить ширину меню')}
              onMouseDown={startResize}
            />
          )}
        </Sider>

        {/* minWidth: 0 обязателен: без него флекс-элемент не даёт широкой
            таблице сжаться, страница уезжает вбок и первые колонки
            оказываются под меню. */}
        <Content style={{ margin: 24, minWidth: 0 }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
